import { useState, useMemo, useEffect, useCallback } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { Button, Card, Input, cn, Badge, Checkbox } from '@pantry/ui';
import {
  useAdminProducts,
  type AdminProductQueryParams,
  type AdminProduct,
  type ProductSource,
} from '@pantry/api-client';

const PRODUCT_SOURCES = ['openfoodfacts', 'user', 'manual'] as const;
import {
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CubeIcon,
  PhotoIcon,
  TagIcon,
  GlobeAltIcon,
  UserIcon,
  PencilSquareIcon,
  ArrowsRightLeftIcon,
} from '@heroicons/react/24/outline';
import { ProductDetailsSheet } from './product-details-sheet';
import { MergeProductsDialog } from './merge-products-dialog';

function getSourceLabels(t: ReturnType<typeof useLingui>['t']): Record<ProductSource, string> {
  return {
    openfoodfacts: t`Open Food Facts`,
    user: t`User Contributed`,
    manual: t`Manual Entry`,
  };
}

const SOURCE_COLORS: Record<ProductSource, string> = {
  openfoodfacts: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  user: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  manual: 'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300',
};

const SOURCE_ICONS: Record<ProductSource, React.ReactNode> = {
  openfoodfacts: <GlobeAltIcon className="h-3.5 w-3.5" />,
  user: <UserIcon className="h-3.5 w-3.5" />,
  manual: <PencilSquareIcon className="h-3.5 w-3.5" />,
};

interface ProductRowProps {
  product: AdminProduct;
  isSelected: boolean;
  onSelect: (product: AdminProduct) => void;
  onToggleSelect: (product: AdminProduct, checked: boolean) => void;
}

function ProductRow({ product, isSelected, onSelect, onToggleSelect }: ProductRowProps) {
  const { t, i18n } = useLingui();
  const SOURCE_LABELS = getSourceLabels(t);
  return (
    <tr
      className="group border-b border-border/50 last:border-0 hover:bg-muted/50 cursor-pointer transition-colors"
      onClick={() => onSelect(product)}
    >
      {/* Checkbox */}
      <td className="px-4 py-3 w-12" onClick={(e) => e.stopPropagation()}>
        <Checkbox
          checked={isSelected}
          onCheckedChange={(checked) => onToggleSelect(product, checked === true)}
          aria-label={t`Select ${product.name}`}
        />
      </td>

      {/* Product info */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          {product.imageUrl ? (
            <img
              src={product.imageUrl}
              alt={product.name}
              className="h-10 w-10 rounded-lg object-cover bg-muted"
            />
          ) : (
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
              <CubeIcon className="h-5 w-5 text-muted-foreground" />
            </div>
          )}
          <div className="min-w-0">
            <span className="font-medium text-foreground truncate block">{product.name}</span>
            {product.brand && (
              <span className="text-sm text-muted-foreground truncate block">{product.brand}</span>
            )}
          </div>
        </div>
      </td>

      {/* Barcode */}
      <td className="px-4 py-3 text-sm text-muted-foreground hidden sm:table-cell">
        {product.barcode || (
          <span className="text-amber-500 flex items-center gap-1">
            <TagIcon className="h-3.5 w-3.5" />
            <Trans>No barcode</Trans>
          </span>
        )}
      </td>

      {/* Source */}
      <td className="px-4 py-3">
        <Badge
          variant="secondary"
          className={cn('font-medium text-xs', SOURCE_COLORS[product.source])}
        >
          {SOURCE_LABELS[product.source]}
        </Badge>
      </td>

      {/* Category */}
      <td className="px-4 py-3 text-sm text-muted-foreground hidden md:table-cell">
        {product.category || (
          <span className="text-amber-500">
            <Trans>No category</Trans>
          </span>
        )}
      </td>

      {/* Items */}
      <td className="px-4 py-3 text-sm text-muted-foreground hidden lg:table-cell">
        {product.itemCount.toLocaleString(i18n.locale)}
      </td>

      {/* Last used */}
      <td className="px-4 py-3 text-sm text-muted-foreground hidden lg:table-cell">
        {product.lastUsedAt
          ? formatRelativeTime(new Date(product.lastUsedAt), t, i18n.locale)
          : t`Never`}
      </td>
    </tr>
  );
}

function formatRelativeTime(
  date: Date,
  t: ReturnType<typeof useLingui>['t'],
  locale: string
): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return t`Just now`;
  if (diffMins < 60) return t`${diffMins}m ago`;
  if (diffHours < 24) return t`${diffHours}h ago`;
  // i18n: "d" abbreviates "days", relative time
  if (diffDays < 7) return t`${diffDays}d ago`;
  if (diffDays < 30) return t`${Math.floor(diffDays / 7)}w ago`;
  return date.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
}

type FilterOption = ProductSource | 'all' | 'missing_barcode' | 'missing_image' | 'no_category';

function getFilterOptions(
  t: ReturnType<typeof useLingui>['t']
): { value: FilterOption; label: string; icon?: React.ReactNode }[] {
  return [
    { value: 'all', label: t`All Products` },
    { value: 'openfoodfacts', label: t`Open Food Facts`, icon: SOURCE_ICONS.openfoodfacts },
    { value: 'user', label: t`User Contributed`, icon: SOURCE_ICONS.user },
    { value: 'manual', label: t`Manual Entry`, icon: SOURCE_ICONS.manual },
    {
      value: 'missing_barcode',
      label: t`Missing Barcode`,
      icon: <TagIcon className="h-3.5 w-3.5" />,
    },
    {
      value: 'missing_image',
      label: t`Missing Image`,
      icon: <PhotoIcon className="h-3.5 w-3.5" />,
    },
    { value: 'no_category', label: t`No Category`, icon: <CubeIcon className="h-3.5 w-3.5" /> },
  ];
}

export function AdminProducts() {
  const { t, i18n } = useLingui();
  const FILTER_OPTIONS = getFilterOptions(t);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState<FilterOption>('all');
  const [page, setPage] = useState(1);
  const [selectedProduct, setSelectedProduct] = useState<AdminProduct | null>(null);
  const [selectedForMerge, setSelectedForMerge] = useState<Map<string, AdminProduct>>(new Map());
  const [showMergeDialog, setShowMergeDialog] = useState(false);
  const limit = 25;

  // Debounce search input and clear selections when search changes
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
      setSelectedForMerge(new Map());
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Build query params
  const queryParams: AdminProductQueryParams = useMemo(() => {
    const params: AdminProductQueryParams = {
      page,
      limit,
      sortBy: 'updatedAt',
      sortOrder: 'desc',
    };

    if (debouncedSearch) {
      params.search = debouncedSearch;
    }

    // Handle filter
    if (['missing_barcode', 'missing_image', 'no_category'].includes(activeFilter)) {
      params.quality = activeFilter as AdminProductQueryParams['quality'];
    } else if (activeFilter !== 'all' && PRODUCT_SOURCES.includes(activeFilter as ProductSource)) {
      params.source = activeFilter as ProductSource;
    }

    return params;
  }, [page, limit, debouncedSearch, activeFilter]);

  const { data, isLoading, error } = useAdminProducts(queryParams);

  const handleToggleSelect = useCallback((product: AdminProduct, checked: boolean) => {
    setSelectedForMerge((prev) => {
      const next = new Map(prev);
      if (checked) {
        next.set(product.id, product);
      } else {
        next.delete(product.id);
      }
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      const products = data?.data;
      if (checked && products) {
        const all = new Map<string, AdminProduct>();
        products.forEach((p) => all.set(p.id, p));
        setSelectedForMerge(all);
      } else {
        setSelectedForMerge(new Map());
      }
    },
    [data]
  );

  const selectedProducts = Array.from(selectedForMerge.values());
  const allSelected =
    data?.data && data.data.length > 0 && selectedForMerge.size === data.data.length;
  const someSelected = selectedForMerge.size > 0 && !allSelected;

  const handleMergeComplete = useCallback(() => {
    setShowMergeDialog(false);
    setSelectedForMerge(new Map());
  }, []);

  const handleFilterChange = (filter: FilterOption) => {
    setActiveFilter(filter);
    setPage(1);
    setSelectedForMerge(new Map());
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    setSelectedForMerge(new Map());
  };

  const totalPages = data?.pagination.totalPages || 1;
  const total = data?.pagination.total || 0;
  const startItem = (page - 1) * limit + 1;
  const endItem = Math.min(page * limit, total);

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10">
            <CubeIcon className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-semibold text-foreground">
              <Trans>Products</Trans>
            </h1>
            <p className="text-muted-foreground">
              <Trans>Manage the global product catalog</Trans>
            </p>
          </div>
        </div>

        {/* Merge button - shown when 2+ products selected */}
        {selectedProducts.length >= 2 && (
          <Button onClick={() => setShowMergeDialog(true)} className="gap-2">
            <ArrowsRightLeftIcon className="h-4 w-4" />
            <Trans>Merge {selectedProducts.length} Products</Trans>
          </Button>
        )}
      </div>

      {/* Search and filters */}
      <Card className="rounded-xl">
        <div className="p-4 space-y-4">
          {/* Search input */}
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              type="text"
              placeholder={t`Search products by name, barcode, or brand...`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 rounded-xl"
            />
          </div>

          {/* Filter pills */}
          <div className="flex flex-wrap gap-2">
            {FILTER_OPTIONS.map((option) => (
              <button
                key={option.value}
                onClick={() => handleFilterChange(option.value)}
                className={cn(
                  'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                  activeFilter === option.value
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground'
                )}
              >
                {option.icon}
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </Card>

      {/* Products table */}
      <Card className="rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="h-10 w-10 animate-spin rounded-full border-3 border-primary border-t-transparent" />
          </div>
        ) : error ? (
          <div className="p-6 text-center">
            <div className="rounded-xl bg-destructive/10 border border-destructive/20 p-4 text-destructive">
              <Trans>Failed to load products: {error.message}</Trans>
            </div>
          </div>
        ) : data?.data.length === 0 ? (
          <div className="p-12 text-center">
            <CubeIcon className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
            <p className="text-muted-foreground">
              {debouncedSearch
                ? t`No products found matching "${debouncedSearch}"`
                : t`No products found`}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="px-4 py-3 w-12">
                      <Checkbox
                        checked={allSelected || someSelected}
                        onCheckedChange={handleSelectAll}
                        aria-label={t`Select all products`}
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                      <Trans>Product</Trans>
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground hidden sm:table-cell">
                      <Trans>Barcode</Trans>
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                      <Trans>Source</Trans>
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground hidden md:table-cell">
                      <Trans>Category</Trans>
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground hidden lg:table-cell">
                      <Trans>Items</Trans>
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground hidden lg:table-cell">
                      <Trans>Last Used</Trans>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data?.data.map((product) => (
                    <ProductRow
                      key={product.id}
                      product={product}
                      isSelected={selectedForMerge.has(product.id)}
                      onSelect={setSelectedProduct}
                      onToggleSelect={handleToggleSelect}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted/20">
              <p className="text-sm text-muted-foreground">
                <Trans>
                  Showing {startItem}-{endItem} of {total.toLocaleString(i18n.locale)} products
                </Trans>
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="rounded-lg"
                >
                  <ChevronLeftIcon className="h-4 w-4" />
                  <span className="sr-only">
                    <Trans>Previous</Trans>
                  </span>
                </Button>
                <span className="text-sm text-muted-foreground">
                  <Trans>
                    Page {page} of {totalPages}
                  </Trans>
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(Math.min(totalPages, page + 1))}
                  disabled={page >= totalPages}
                  className="rounded-lg"
                >
                  <ChevronRightIcon className="h-4 w-4" />
                  <span className="sr-only">
                    <Trans>Next</Trans>
                  </span>
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>

      {/* Product details side sheet */}
      {selectedProduct && (
        <ProductDetailsSheet product={selectedProduct} onClose={() => setSelectedProduct(null)} />
      )}

      {/* Merge products dialog */}
      {showMergeDialog && selectedProducts.length >= 2 && (
        <MergeProductsDialog
          products={selectedProducts}
          onClose={() => setShowMergeDialog(false)}
          onMerged={handleMergeComplete}
        />
      )}
    </div>
  );
}
